import * as React from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import type { ChatDirectoryEntry } from '@bits/shared/zod';
import { api } from '@/lib/api';
import { errorMessage } from '@/lib/errors';
import { useChat } from '@/lib/chat-provider';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field } from '@/components/ui/field';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { PeoplePicker } from './PeoplePicker';

export function NewGroupDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.ReactElement {
  const navigate = useNavigate();
  const { meId } = useChat();
  const [title, setTitle] = React.useState('');
  const [people, setPeople] = React.useState<ChatDirectoryEntry[]>([]);

  React.useEffect(() => {
    if (!open) {
      setTitle('');
      setPeople([]);
    }
  }, [open]);

  const create = useMutation({
    mutationFn: () =>
      api.chatCreateGroup({ title: title.trim(), memberIds: people.map((p) => p.id) }),
    onSuccess: async ({ id }) => {
      onOpenChange(false);
      await navigate({ to: '/chat/$conversationId', params: { conversationId: id } });
    },
  });

  const canSubmit = title.trim().length > 0 && people.length > 0 && !create.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form
          className="contents"
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) create.mutate();
          }}
        >
          <DialogHeader>
            <DialogTitle>New group</DialogTitle>
            <DialogDescription>
              Everyone you add sees the whole conversation from this point on.
            </DialogDescription>
          </DialogHeader>
          <Field label="Group name" required>
            <Input
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={120}
              placeholder="e.g. Design reviews"
            />
          </Field>
          <Field label="People" required>
            <PeoplePicker excludeIds={meId ? [meId] : []} selected={people} onChange={setPeople} />
          </Field>
          {create.isError ? (
            <p className="text-sm text-[hsl(var(--destructive))]">{errorMessage(create.error)}</p>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {create.isPending ? 'Creating…' : 'Create group'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
